// Message on a booking (#333, MS-1): opens a box to write to the other person on the booking.
// Sending posts the booking id and the text to POST /conversations, which starts the pair's
// conversation or continues it. The server checks the booking is the caller's; the screen only
// saves a round trip on empty or too long text.
//
// onStarted receives the conversation the server returns, so the dashboard can open it.
import React from 'react';
import { api } from '../../lib/api';
import { Btn } from '../index';
import { MESSAGE_MAX } from './ConversationThread';
import { palette as COLORS, alpha, type } from '../../design';

export const MESSAGE_SEND_FAILED = 'We could not send your message. Check your connection and send it again.';

const FONT = "'Plus Jakarta Sans', sans-serif";

// A refusal (400, 403, 404, 429) carries the server's own words; anything else reads as a failed
// send the person can retry.
function sendErrorMessage(err) {
  if (err?.status && err.status < 500 && err.data?.message) return err.data.message;
  return MESSAGE_SEND_FAILED;
}

function ComposeDialog({ bookingId, otherName, onSent, onClose }) {
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const id = React.useId();
  const headingId = `${id}-heading`;
  const textId = `${id}-text`;

  const trimmed = text.trim();
  const canSend = trimmed.length > 0 && trimmed.length <= MESSAGE_MAX && !busy;

  const send = async () => {
    if (!canSend) return;
    setBusy(true);
    setError(null);
    try {
      const data = await api.post('/conversations', { bookingId, text: trimmed });
      onSent(data.conversation);
    } catch (err) {
      console.error('[messages] could not start the conversation', err?.status ?? err);
      setError(sendErrorMessage(err));
      setBusy(false);
    }
  };

  // The box to type in has focus when the dialog opens.
  const textRef = React.useRef(null);
  React.useEffect(() => { textRef.current?.focus(); }, []);

  // Escape closes the dialog, except while the message is on its way.
  const busyRef = React.useRef(busy);
  busyRef.current = busy;
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busyRef.current) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The dialog element holds the heading, the box and every button: aria-modal hides anything
  // outside it from a screen reader, so the buttons cannot sit in a separate footer.
  return (
    <div
      onClick={busy ? undefined : onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 2000, background: alpha('espresso', 'scrim'), backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box', padding: '28px 28px 20px', background: COLORS.ivory, borderRadius: 20, boxShadow: '0 24px 80px ' + alpha('espresso', 'shadow') }}
      >
        <button type="button" aria-label="Close" onClick={onClose} disabled={busy}
          style={{ position: 'absolute', top: 16, right: 16, background: COLORS.warmGrey, border: '1px solid ' + COLORS.border, borderRadius: '50%', width: 32, height: 32, cursor: busy ? 'default' : 'pointer', fontSize: type.h4, color: COLORS.walnut, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          ✕
        </button>
        <h2 id={headingId} style={{ fontFamily: FONT, fontSize: type.h3, fontWeight: 700, color: COLORS.espresso, margin: 0, paddingRight: 40 }}>
          Message {otherName}
        </h2>
        <p style={{ fontFamily: FONT, fontSize: type.body, color: COLORS.walnut, margin: 0, lineHeight: 1.5 }}>
          You both see this conversation under Messages on your dashboards.
        </p>
        <label htmlFor={textId} style={{ fontFamily: FONT, fontSize: type.body, fontWeight: 600, color: COLORS.espresso }}>Your message</label>
        <textarea
          ref={textRef}
          id={textId}
          value={text}
          maxLength={MESSAGE_MAX}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          placeholder="For example: Is the session in Japanese only?"
          style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid ' + COLORS.border, fontFamily: FONT, fontSize: type.body, color: COLORS.espresso, resize: 'vertical', outline: 'none', boxSizing: 'border-box', background: COLORS.white }}
        />
        <span style={{ fontFamily: FONT, fontSize: type.label, color: COLORS.driftwood, textAlign: 'right' }}>{text.length} / {MESSAGE_MAX}</span>
        {error && (
          <p role="alert" style={{ fontFamily: FONT, fontSize: type.body, color: COLORS.errorRed, margin: 0, lineHeight: 1.5 }}>{error}</p>
        )}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end', borderTop: '1px solid ' + COLORS.border, paddingTop: 16, marginTop: 4 }}>
          <Btn size="sm" variant="subtle" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn size="sm" onClick={send} disabled={!canSend}>{busy ? 'Sending…' : 'Send'}</Btn>
        </div>
      </div>
    </div>
  );
}

export function MessageButton({ bookingId, otherName, onStarted }) {
  const [open, setOpen] = React.useState(false);
  // Btn takes no ref, so the wrapper finds the button to give focus back to.
  const wrapRef = React.useRef(null);

  const handleSent = (conversation) => {
    setOpen(false);
    onStarted?.(conversation);
  };

  // Closed without sending: focus goes back to Message, where the person was.
  const handleClose = () => {
    setOpen(false);
    wrapRef.current?.querySelector('button')?.focus();
  };

  return (
    <>
      <span ref={wrapRef} style={{ display: 'inline-flex' }}>
        <Btn size="sm" variant="ghost" onClick={() => setOpen(true)}>Message</Btn>
      </span>
      {open && (
        <ComposeDialog bookingId={bookingId} otherName={otherName} onSent={handleSent} onClose={handleClose} />
      )}
    </>
  );
}
